import manifest from "../../../../config/questionnaire-question-capabilities.json";
import type { QuestionType } from "./index";
import type { PlanVerbatimContent } from "./plan";
import { QUESTION_TYPE_IDS, QUESTION_TYPE_REGISTRY } from "./registry";

/**
 * What each question type supports, shared with Python
 * (`apps/python-api/models/questionnaire_plan/question_capabilities.py`,
 * which documents each fact). Every TypeScript reader of these facts derives
 * from here.
 */
export interface QuestionCapabilities {
  /** AUTHOR writes a member sequence, so a shared-list claim can be checked. */
  sharedListCarrying: boolean;
  /** Its typed outcomes are options a categorical condition can name by index. */
  outcomeConditionSource: boolean;
  /** It takes a bounded or unbounded selection policy. */
  selectionPolicy: boolean;
  /**
   * Its answers (a select's options, a categorical grid's categories) are
   * shown fixed or randomized (`optionOrder`), and an option list's
   * `keepLastPositions` can pin answers last. A ranked list has no such
   * choice: its items are always shown shuffled (the compiler's ranking element).
   */
  optionOrder: boolean;
  /**
   * Its answers (a select's options, a categorical grid's categories) may be
   * declared one ordinal sequence, in list order (`ordered`).
   */
  orderedAnswers: boolean;
  /** Its answer list may be carried forward from an earlier question's answers (`choicesFrom`). */
  choicesFromTarget: boolean;
  /** Its answers may be carried forward to a later question's answer list. */
  choicesFromSource: boolean;
  /** Its answer options or grid categories may be the tested-asset roster (`assetChoiceIntent`). */
  assetChoiceSurface: boolean;
  /** A verdict (a winner among the tested assets) can be read from its answers. */
  verdictSurface: boolean;
  /** Typed non-scored answers it may offer (per measure on a grid); null sets no count. */
  maxNonScoredStates: number | null;
  /** The literal fields AUTHOR's closed response for the type can carry. */
  verbatimFields: ReadonlyArray<keyof PlanVerbatimContent>;
}

type QuestionCapabilityManifest = {
  schemaVersion: "questionnaire-question-capabilities.v1";
  matrixMaxColumns: number;
  researchQuestionFloor: number;
  researchQuestionCeiling: number;
  minutesPerQuestion: number;
  assetChoiceMinAssets: number;
  minChoiceMembers: number;
  brandCompetitorMax: number;
  questionTypes: Record<QuestionType, QuestionCapabilities>;
};

const parsed = manifest as unknown as QuestionCapabilityManifest;
if (
  parsed.schemaVersion !== "questionnaire-question-capabilities.v1" ||
  !Number.isInteger(parsed.matrixMaxColumns) ||
  parsed.matrixMaxColumns < 1 ||
  !Number.isInteger(parsed.researchQuestionFloor) ||
  !Number.isInteger(parsed.researchQuestionCeiling) ||
  parsed.researchQuestionFloor < 1 ||
  parsed.researchQuestionFloor > parsed.researchQuestionCeiling ||
  typeof parsed.minutesPerQuestion !== "number" ||
  !(parsed.minutesPerQuestion > 0) ||
  !Number.isInteger(parsed.assetChoiceMinAssets) ||
  parsed.assetChoiceMinAssets < 1 ||
  !Number.isInteger(parsed.minChoiceMembers) ||
  parsed.minChoiceMembers < 1 ||
  !Number.isInteger(parsed.brandCompetitorMax) ||
  parsed.brandCompetitorMax < 0 ||
  JSON.stringify(Object.keys(parsed.questionTypes)) !==
    JSON.stringify(QUESTION_TYPE_IDS)
) {
  throw new TypeError(
    "Questionnaire question-capability registry does not match QuestionType.",
  );
}

/**
 * Most rating measures (matrix_rating) or categories (matrix_categorical) in
 * one grid. It matches the deck's matrix heatmap limit, so a grid the builder
 * accepts is one the report can draw.
 */
export const MATRIX_MAX_COLUMNS: number = parsed.matrixMaxColumns;

/**
 * Fewest research questions any draft may have: 1. There is no product
 * minimum beyond "at least one question"; only an empty set is invalid.
 * Python's PLAN stage reads the same key.
 */
export const DEFAULT_RESEARCH_QUESTION_FLOOR: number =
  parsed.researchQuestionFloor;

/** Hard ceiling on research questions; Python's PLAN stage reads the same key. */
export const RESEARCH_QUESTION_CEILING: number = parsed.researchQuestionCeiling;

/**
 * Minutes a research question adds to the questionnaire's length (the
 * registry's `minutesPerQuestion`). Python's revise and REPLAN projections
 * read the same key (`models.questionnaire_plan.question_capabilities`).
 */
export const MINUTES_PER_QUESTION: number = parsed.minutesPerQuestion;

/**
 * The questionnaire's length in minutes, as the builder header, Review and the
 * chat reply all state it: `MINUTES_PER_QUESTION` a question, rounded half up,
 * never under one minute. Python's `estimated_questionnaire_minutes` is the
 * same arithmetic, held to it by a parity test (gate 2026-10-16: the
 * frontend's copy was the only one, NA-09, MT-09; final30: revise could not
 * measure a limit in minutes, LP-04, LP-21).
 */
export function estimatedQuestionnaireMinutes(questionCount: number): number {
  return Math.max(1, Math.round(questionCount * MINUTES_PER_QUESTION));
}

/**
 * Fewest tested assets of one family an asset choice offers, and the fewest
 * participants a comparison needs; Python's PLAN stage reads the same key.
 */
export const ASSET_CHOICE_MIN_ASSETS: number = parsed.assetChoiceMinAssets;

/** Fewest answer options a choice, or categories a categorical grid, may offer. */
export const MIN_CHOICE_MEMBERS: number = parsed.minChoiceMembers;

/**
 * Most competitors a study's brand facts may name, until brand competitors
 * are assets. Python reads the same key and gives every model writer of
 * brand facts a `maxItems` from it.
 */
export const BRAND_COMPETITOR_MAX: number = parsed.brandCompetitorMax;

/** Per question type, in `QUESTION_TYPE_IDS` order. */
export const QUESTION_CAPABILITIES: Readonly<
  Record<QuestionType, QuestionCapabilities>
> = Object.freeze(parsed.questionTypes);

function typesWhere(
  holds: (type: QuestionType) => boolean,
): ReadonlySet<QuestionType> {
  return new Set(QUESTION_TYPE_IDS.filter(holds));
}

const content = (type: QuestionType) => QUESTION_TYPE_REGISTRY[type].content;

// What content a type's response has is the question-type registry's content
// contract (`registry.ts`), which build, preview and the twin test already
// read. The grid, rating-design and option-list sets derive from it.

/** A grid with rows and columns (`matrixIntent`). */
export const MATRIX_QUESTION_TYPES = typesWhere(
  (type) => content(type).requiresColumns,
);
/** It takes a rating scale design (`ratingUnit`, `pointLabelsRequired`). */
export const RATING_DESIGN_QUESTION_TYPES = typesWhere(
  (type) => content(type).requiresScale,
);
/**
 * Its answers are one option list, not a grid's categories (literal or named
 * option labels, `outcomePositions`, a shared choice list).
 */
export const OPTION_LIST_QUESTION_TYPES = typesWhere(
  (type) => content(type).requiresChoices && !content(type).requiresColumns,
);
/** Its answers, or a grid's categories, are shown fixed or randomized (`optionOrder`). */
export const OPTION_ORDER_QUESTION_TYPES = typesWhere(
  (type) => QUESTION_CAPABILITIES[type].optionOrder,
);
/** Its answers may be declared one ordinal sequence (`ordered`). */
export const ORDERED_ANSWER_QUESTION_TYPES = typesWhere(
  (type) => QUESTION_CAPABILITIES[type].orderedAnswers,
);
/** It takes a bounded or unbounded selection policy. */
export const SELECTION_POLICY_QUESTION_TYPES = typesWhere(
  (type) => QUESTION_CAPABILITIES[type].selectionPolicy,
);
/** Its typed outcomes are options a categorical condition can name by index. */
export const OUTCOME_CONDITION_SOURCE_QUESTION_TYPES = typesWhere(
  (type) => QUESTION_CAPABILITIES[type].outcomeConditionSource,
);
/** Its answer options or grid categories may be the tested-asset roster. */
export const ASSET_CHOICE_SURFACE_QUESTION_TYPES = typesWhere(
  (type) => QUESTION_CAPABILITIES[type].assetChoiceSurface,
);
